import { apiDelete, apiGet, apiPost, apiPut, buildFormData } from '../api.utils'
import { IResponseCommon } from "../common.types"
import {
    IResponseListenerAddressSuggestions,
    IResponseListenerCheckField,
    IResponseListenerCheckSnils,
    IResponseListenerFiles,
    IResponseListenerJourney,
    IResponseListenerSave,
    IResponseListeners,
    IResponseListenersCard,
} from "./api.types"
import { IListenerFormSendedData } from "./state.types"


const route = '/listeners'
const LISTENER_FILE_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000

export const getListenerAddressSuggestions = (query: string, signal?: AbortSignal): Promise<IResponseListenerAddressSuggestions> =>
    apiPost<IResponseListenerAddressSuggestions>(`${route}/getListenerAddressSuggestions`, { signal }, { query })

export const getListenerRecognitionFile = async (filePath: string, name: string, signal: AbortSignal): Promise<File> => {
    const url = new URL(filePath, window.location.href)
    if (!/^https?:$/.test(url.protocol) || !/^\/assets\/listeners\/\d+\/[^/]+$/.test(url.pathname)) throw new Error('Некорректная ссылка на файл слушателя.')
    const response = await fetch(url.href, { signal, credentials: 'omit', redirect: 'error' })
    if (!response.ok) throw new Error('Не удалось открыть выбранный файл.')
    const maximum = 12 * 1024 * 1024
    if (Number(response.headers.get('content-length')) > maximum) {
        await response.body?.cancel()
        throw new Error('Распознавание: до 12 МБ на файл.')
    }
    const reader = response.body?.getReader()
    let blob: Blob
    if (reader) {
        const chunks: BlobPart[] = []
        let size = 0
        try {
            while (true) {
                const chunk = await reader.read()
                if (chunk.done) break
                size += chunk.value.byteLength
                if (size > maximum) { await reader.cancel(); throw new Error('Распознавание: до 12 МБ на файл.') }
                chunks.push(chunk.value.slice().buffer as ArrayBuffer)
            }
        } finally { reader.releaseLock() }
        blob = new Blob(chunks, { type: response.headers.get('content-type') || 'application/octet-stream' })
    } else blob = await response.blob()
    if (blob.size > maximum) throw new Error('Распознавание: до 12 МБ на файл.')
    return new File([blob], name, { type: blob.type })
}

/** Проверка по номеру телефона, существование слушателя */
export const getListenerCheckPhone = (listenerID: number, clientPhone: string): Promise<IResponseListenerCheckField> =>
    apiGet<IResponseListenerCheckField>(`${route}/getListenerCheckPhone`, { params: { listenerID, clientPhone } })

/** Проверка по СНИЛС, существование слушателя */
export const getListenerCheckSnils = (listenerID: number, snils: string, signal?: AbortSignal): Promise<IResponseListenerCheckSnils> =>
    apiGet<IResponseListenerCheckSnils>(`${route}/getListenerCheckSnils`, { params: { listenerID, snils }, signal })



/** Получение списка слушателей */
export const getListeners = (pageNumber: number, search?: string, signal?: AbortSignal): Promise<IResponseListeners> =>
    apiGet<IResponseListeners>(`${route}/getListeners`, { params: { pageNumber, search }, signal })

/** Получение полного цикла слушателя */
export const getListenerJourney = (listenerID: number, signal?: AbortSignal): Promise<IResponseListenerJourney> =>
    apiGet<IResponseListenerJourney>(`${route}/getListenerJourney`, { params: { listenerID }, signal })

/** Получение списка файлов слушателя */
export const getListenerFiles = (listenerID: number): Promise<IResponseListenerFiles> =>
    apiGet<IResponseListenerFiles>(`${route}/getListenerFiles`, { params: { listenerID } })



/** Получение данных по анкете слушателя */
export const getListenerCard = (listenerID: number): Promise<IResponseListenersCard> =>
    apiGet<IResponseListenersCard>(`${route}/getListenerCard`, { params: { listenerID } })

/** Сохранение данных по анкете слушателя */
export const setListenerCardSave = (listenerID: number, data: IListenerFormSendedData, files?: File[]): Promise<IResponseListenerSave> =>
    apiPut<IResponseListenerSave>(
        `${route}/setListenerCardSave`,
        {
            params: { listenerID },
            headers: { 'Content-Type': 'multipart/form-data' },
            ...(files?.length ? { timeout: LISTENER_FILE_UPLOAD_TIMEOUT_MS } : {}),
        },
        buildFormData({ listenerData: JSON.stringify(data), docs: files })
    )



/** Удаление файлов слушателя */
export const setListenerDeleteFiles = (listenerID: number, fileID: number): Promise<IResponseCommon> =>
    apiDelete<IResponseCommon>(`${route}/setListenerDeleteFiles`, { params: { listenerID, fileID } })

/** Загрузка файлов слушателя */
export const setListenerUploadFiles = (listenerID: number, files: File[]): Promise<IResponseCommon> =>
    apiPost<IResponseCommon>(
        `${route}/setListenerUploadFiles`,
        {
            params: { listenerID },
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: LISTENER_FILE_UPLOAD_TIMEOUT_MS,
        },
        buildFormData({ docs: files })
    )